import { flattenObject } from '@apparel/core/utils/objects/flattenObject';
import { set } from 'lodash';
import { ReturnOnlyField } from './ReturnOnlyField';

export class ReturnOnly {
    public static from(tree: any) {
        if (Array.isArray(tree)) {
            tree = this.arrayToObject(tree);
        }

        if (typeof tree === 'string') {
            return this.parse(tree);
        }

        if (tree instanceof ReturnOnlyField) {
            return tree;
        }

        return new ReturnOnlyField().setRoot().from(tree);
    }

    public static parse(value: any) {
        if (value instanceof ReturnOnlyField) {
            return value;
        }

        if (!value || typeof value !== 'string') {
            return null;
        }

        const root = new ReturnOnlyField().setRoot();
        const tree = this.createReturnOnlyFieldsTree(value);
        return root.from(tree);
    }

    private static createReturnOnlyFieldsTree(value: string): any {
        const length = value.length;
        const $value = `${value ?? ''}`.replace(/\r/g, '');
        let word = '';
        let prefix = '';

        let tree = [];

        for (let i = 0; i < length; i++) {
            const char = $value[i];
            switch (char) {
                case ',':
                case '\n':
                    tree = this.append(tree, prefix, word);
                    word = '';
                    break;

                case '{':
                    prefix = `${prefix}.${word}`;
                    word = '';
                    break;

                case '}':
                    const prefixSegments = prefix.split('.');
                    tree = this.append(tree, prefix, word);
                    word = '';

                    prefixSegments.pop();
                    prefix = prefixSegments.join('.');
                    break;
                case ' ':
                    break;
                default:
                    word += char;
            }
        }

        if (word) {
            tree = this.append(tree, prefix, word);
        }

        return tree;
    }

    /**
     * Convert array to object
     * @param fields
     */
    public static arrayToObject(fields: any[] = []) {
        return fields.reduce((obj, field) => {
            if (typeof field === 'string') {
                obj[field] = true;
            } else if (typeof field === 'object') {
                if (Array.isArray(field)) {
                    obj[field[0]] = this.arrayToObject(Array.isArray(field[1]) ? field[1] : field.slice(1));
                }
            }
            return obj;
        }, {});
    }

    private static append(tree, prefix, word) {
        const item = `${prefix}.${word}`.trim().replace(/^\./g, '').replace(/\.\./g, '.');

        if (item !== '' && !item.endsWith('.')) {
            set(tree, item, true);
        }

        return tree;
    }

    public static stringify(tree: any) {
        return this.from(tree).toString();
    }

    public static merge(source: any, ...targets: any[]): ReturnOnlyField {
        const source$ = this.from(source).toJSON();
        const finalObject = targets.reduce((prevValue, value) => {
            const value$ = this.from(value).toJSON();
            const obj = flattenObject(value$);

            Object.entries(obj).forEach(([key, val]) => {
                set(source$, key, val);
            });

            return source$;
        }, source$);

        return ReturnOnly.from(finalObject);
    }
}
